import { ToastService } from '../../../../../base/services/toast.service';
import { SpinnerService } from '../../../../../base/spinner/spinner.service';
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FileInfo, SelectedEventArgs, Uploader, UploaderModule } from '@syncfusion/ej2-angular-inputs';
import { FileBase64DTO, ImportFixDTO, ImportProtocolClient, ImportProtocolDTO, ImportSuccessState } from 'src/app/base/models/models.swagger.generated';
import { FileDownloadHelper } from 'src/app/base/shared/file-download.helper';
import { FileFormatHelper } from 'src/app/base/shared/file-format.helper';
import { MimeTypeHelper } from 'src/app/base/shared/mime-type.helper';
import { TranslateModule } from '@ngx-translate/core';


@Component({
    selector: 'app-import-configuration-protocol-edit-form-file-handling',
    templateUrl: './import-configuration-protocol-edit-form-file-handling.component.html',
    styleUrls: ['./import-configuration-protocol-edit-form-file-handling.component.scss'],
    imports: [UploaderModule, TranslateModule]
})
export class ImportConfigurationProtocolEditFormFileHandlingComponent {
  @ViewChild('defaultupload') public defaultupload: Uploader;

  @Input()
  public set protocol(value: ImportProtocolDTO) {
    if (this._protocol != value) {
      this._protocol = value;
      this.canUploadFile = this._protocol?.successState === ImportSuccessState.Error;
      this.resetFileData();
      this.defaultupload?.clearAll();
      this.allowedExtensions = FileFormatHelper.getAllowedFileExtensionsForCorrectionFiles(this._protocol?.fileFormat);
    }
  }

  @Output()
  public import: EventEmitter<ImportFixDTO> = new EventEmitter<ImportFixDTO>();

  public get canImport(): boolean {
    return this.canUploadFile && !!this._base64Data;
  }

  public get hasFixedImportFile(): boolean {
    return !!this._protocol?.correctedFileMediaId;
  }

  public get hasImportFile(): boolean {
    return !!this._protocol?.importFileMediaId;
  }

  public get hasOriginalFile(): boolean {
    return !!this._protocol?.originalFileMediaId;
  }

  public get hasOriginalArchiveFile(): boolean {
    return !!this._protocol?.originalArchiveFileUrl;
  }

  public get originalArchiveFileUrl(): string {
    return this._protocol?.originalArchiveFileUrl;
  }

  public allowedExtensions: string;
  public canUploadFile: boolean;
  private _protocol: ImportProtocolDTO;
  private _base64Data: string;
  private _fileName: string;

  constructor(private protocolClient: ImportProtocolClient,
    private spinnerService: SpinnerService,
    private toastService: ToastService) { }

  public onFileRemove() {
    this.resetFileData();
  }

  public handleFileInput($event: SelectedEventArgs) {
    const files: FileInfo[] = $event.filesData;
    if (!files || files.length < 1) {
      return;
    }
    const file = files[0];
    const reader = new FileReader();
    reader.onload = (event) => {
      let base64Data = event.target.result as string;
      const startIndex = base64Data.search(';base64,');
      if (startIndex > 0) {
        // replace type by cert
        base64Data = 'data:' + MimeTypeHelper.getMimeType(file.type.toLowerCase()) + base64Data.substring(startIndex);
      } else {
        base64Data = 'data:' + MimeTypeHelper.getMimeType(file.type.toLowerCase()) + ';base64,' + base64Data;
      }
      this._base64Data = base64Data;
    };
    reader.readAsDataURL(file.rawFile as File);
    this._fileName = file.name;
  }

  public onImport() {
    this.import.emit(new ImportFixDTO({importConfigurationEntryId: this._protocol.id, base64ImportData: this._base64Data, fileName: this._fileName}));
  }

  public downloadOriginalFile() {
    return this.downloadFile(this._protocol.originalFileMediaId);
  }
  public downloadImportFile() {
    return this.downloadFile(this._protocol.importFileMediaId);
  }
  public downloadFixedImportFile() {
    return this.downloadFile(this._protocol.correctedFileMediaId);
  }

  private downloadFile(fileId: string): boolean {
    if (!fileId) {
      return false;
    }
    this.spinnerService.show();
    this.protocolClient.getImportFile(this._protocol.id, fileId).subscribe({ next: (file: FileBase64DTO) => {
      this.spinnerService.hide();
      FileDownloadHelper.downloadFile(file);
    }, error: () => {
      this.spinnerService.hide();
      this.toastService.showError('i18n.domain.import-protocol.messages.file-download.title', 'i18n.domain.import-protocol.messages.file-download.error-text');
    }});
    return false;
  }

  private resetFileData() {
    this._base64Data = null;
    this._fileName = null;
  }
}
